import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Palette, Sparkles, Car, Sun } from "lucide-react";
import ppfServiceCard from "@/assets/ppf-service-card.jpg.asset.json";
import koenigseggJordanHome from "@/assets/koenigsegg-jordan-home.jpg";
import porscheMatteGray from "@/assets/gallery/porsche-matte-gray.jpg";
import serviceWindowTint from "@/assets/service-window-tint.jpg";
import detailingWork from "@/assets/gallery/detailing-work.jpg";
import netflixDtlKitchen from "@/assets/netflix-dtl-kitchen.jpeg";


const services = [
  {
    icon: Shield,
    title: "Paint Protection Film (PPF)",
    description: "Highest level of self-healing protection against scratches and rock chips. Available in clear, matte, and 200+ colors!",
    href: "/automotive/ppf",
    image: ppfServiceCard.url,
    alt: "Professional PPF installation on a Pagani Utopia at Hyper Haus LA",
  },
  {
    icon: Palette,
    title: "Custom vinyl Wraps",
    description: "Transform your vehicle/fleet with custom-printed vinyl wraps.",
    href: "/automotive/wraps",
    image: koenigseggJordanHome,
    alt: "Koenigsegg Regera with custom Jordan Brand vinyl wrap by Hyper Haus",
  },
  {
    icon: Sun,
    title: "Window Tint",
    description: "Premium window tint for the ultimate heat rejection, UV protection, and privacy.",
    href: "/automotive/tint",
    image: serviceWindowTint,
    alt: "Yellow Lamborghini Diablo with professional window tint by Hyper Haus LA",
  },
  {
    icon: Sparkles,
    title: "Ceramic Coating",
    description: "Professional-grade ceramic coatings for lasting gloss and protection.",
    href: "/automotive/ceramic",
    image: porscheMatteGray,
    alt: "Matte gray Porsche with ceramic coating applied at Hyper Haus",
  },
  {
    icon: Car,
    title: "Detailing",
    description: "Meticulous paint correction and detailing for show-quality finishes.",
    href: "/automotive/detailing",
    image: detailingWork,
    alt: "Professional auto detailing and paint correction at Hyper Haus shop",
  },
];

const Services = () => {
  return (
    <section className="py-24 bg-background">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-primary text-sm uppercase tracking-[0.3em] mb-4">What We Do</p>
          <h2 className="section-heading mb-4">Our Services</h2>
          <p className="section-subheading mx-auto">
            From paint protection to full transformations, we deliver uncompromising quality for the world's most
            exclusive vehicles.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link to={service.href} className="service-card block group h-full">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.alt}
                    loading="lazy"
                    width={406}
                    height={305}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <service.icon className="w-5 h-5 text-primary" />
                    <h3 className="font-display-sm text-xl">{service.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{service.description}</p>
                  <span className="inline-flex items-center gap-2 text-sm text-primary group-hover:gap-3 transition-all">
                    Explore {service.title} <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}

          {/* View All Services Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: services.length * 0.1 }}
          >
            <Link
              to="/services"
              onClick={() => window.scrollTo(0, 0)}
              className="service-card block group h-full relative overflow-hidden"
            >
              <img
                src={netflixDtlKitchen}
                alt="View all Hyper Haus automotive, commercial, residential, and marine services"
                loading="lazy"
                width={406}
                height={609}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 absolute inset-0"
              />
              <div className="absolute inset-0 bg-background/50 flex flex-col items-center justify-center text-center p-6">
                <h3 className="font-display-sm text-3xl md:text-4xl uppercase mb-4">View All Services</h3>
                <span className="inline-flex items-center gap-2 text-sm text-foreground group-hover:gap-3 transition-all font-medium">
                  Explore Full Menu <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Services;
